import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";
import { Navigation } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1200, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  backBtn: { marginBottom: theme.spacing(2), textTransform: "none" },
  summaryCard: {
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.09)",
  },
  summaryField: { display: "flex", flexDirection: "column", gap: 2 },
  summaryLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
  summaryValue: { fontSize: "0.95rem", color: theme.palette.text.primary },
  runHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0, 0.75),
    marginTop: theme.spacing(2),
  },
  runLabel: { fontWeight: 600, fontSize: "0.88rem" },
  errorText: { color: "#c62828", fontSize: "0.8rem", maxWidth: 400, wordBreak: "break-word" },
}));

const STATUS_COLORS = {
  success:         { label: "Success",         style: { backgroundColor: "#e8f5e9", color: "#2e7d32" } },
  partial_failure: { label: "Partial Failure",  style: { backgroundColor: "#fff3e0", color: "#e65100" } },
  failed:          { label: "Failed",           style: { backgroundColor: "#ffebee", color: "#c62828" } },
  running:         { label: "Running",          style: { backgroundColor: "#e3f2fd", color: "#1565c0" } },
};

const ITEM_STATUS_COLORS = {
  success: { label: "Synced",  style: { backgroundColor: "#e8f5e9", color: "#2e7d32" } },
  failed:  { label: "Failed",  style: { backgroundColor: "#ffebee", color: "#c62828" } },
  skipped: { label: "Skipped", style: { backgroundColor: "#f5f5f5", color: "#757575" } },
};

function formatDay(dateStr) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function formatTime(val) {
  if (!val) return "—";
  return new Date(val).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function formatDuration(ms) {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

export function MagentoSyncDayPage(props) {
  const classes = useStyles();
  const date = props.pageRoute?.match?.params?.date;

  const [data, setData]       = useState({ runs: [], items: [] });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack]     = useState({ open: false, msg: "" });

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    authGet(`/magento-sync/day/${date}`).then(({ data: res, error }) => {
      setLoading(false);
      if (error) { setSnack({ open: true, msg: error?.message || "Failed to load" }); return; }
      setData(res || { runs: [], items: [] });
    });
  }, [date]);

  const { runs, items } = data;

  const totalCreated   = runs.reduce((s, r) => s + (r.items_created   || 0), 0);
  const totalUpdated   = runs.reduce((s, r) => s + (r.items_updated   || 0), 0);
  const totalSucceeded = runs.reduce((s, r) => s + (r.items_succeeded || 0), 0);
  const totalAttempted = runs.reduce((s, r) => s + (r.items_attempted || 0), 0);
  const hasRunning = runs.some((r) => r.status === "running");
  const hasFailure = runs.some((r) => r.status === "failed" || r.status === "partial_failure");
  const allFailed  = runs.length > 0 && runs.every((r) => r.status === "failed");
  const dayStatus  = hasRunning ? "running" : hasFailure ? (allFailed ? "failed" : "partial_failure") : "success";
  const sc = STATUS_COLORS[dayStatus] || STATUS_COLORS.running;

  // Group runs into minute buckets (YYYY-MM-DDTHH:MM), merge their items
  const minuteKey = (run) => new Date(run.started_at).toLocaleString("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });

  const itemsByRunId = items.reduce((acc, item) => {
    if (!acc[item.run_id]) acc[item.run_id] = [];
    acc[item.run_id].push(item);
    return acc;
  }, {});

  const minuteGroups = Object.values(
    runs.reduce((acc, run) => {
      const key = minuteKey(run);
      if (!acc[key]) acc[key] = { key, runs: [], items: [] };
      acc[key].runs.push(run);
      acc[key].items.push(...(itemsByRunId[run.id] || []));
      return acc;
    }, {})
  ).reverse();

  if (loading) {
    return <Box className={classes.root}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box className={classes.root}>
      <Button
        size="small"
        variant="outlined"
        className={classes.backBtn}
        onClick={() => Navigation.go("/magento-sync")}
      >
        ← Back to Sync Log
      </Button>

      <Typography variant="h6" gutterBottom>
        {date ? formatDay(date) : "Sync Detail"}
      </Typography>

      {runs.length > 0 && (
        <Paper className={classes.summaryCard}>
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Sync Runs</Typography>
            <Typography className={classes.summaryValue}>{runs.length}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Items</Typography>
            <Typography className={classes.summaryValue}>
              {[
                totalCreated > 0 && `${totalCreated} created`,
                totalUpdated > 0 && `${totalUpdated} updated`,
              ].filter(Boolean).join(", ") || "—"}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Result</Typography>
            <Box style={{ marginTop: 2 }}>
              <Chip
                size="small"
                label={`${sc.label} (${totalSucceeded}/${totalAttempted})`}
                style={sc.style}
              />
            </Box>
          </Box>
        </Paper>
      )}

      {minuteGroups.length === 0 ? (
        <Typography color="textSecondary">No sync runs found for this day.</Typography>
      ) : (
        minuteGroups.map((group) => {
          const groupRuns = group.runs;
          const groupItems = group.items;
          const succeeded = groupRuns.reduce((s, r) => s + (r.items_succeeded || 0), 0);
          const attempted = groupRuns.reduce((s, r) => s + (r.items_attempted || 0), 0);
          const hasRunning = groupRuns.some((r) => r.status === "running");
          const hasFailure = groupRuns.some((r) => r.status === "failed" || r.status === "partial_failure");
          const allFailed  = groupRuns.every((r) => r.status === "failed");
          const gStatus = hasRunning ? "running" : hasFailure ? (allFailed ? "failed" : "partial_failure") : "success";
          const gsc = STATUS_COLORS[gStatus] || STATUS_COLORS.running;
          const totalDuration = groupRuns.reduce((s, r) => s + (r.duration_ms || 0), 0);
          const dur = formatDuration(totalDuration || null);

          return (
            <Box key={group.key}>
              <Box className={classes.runHeader}>
                <Typography className={classes.runLabel}>
                  {formatTime(groupRuns[0].started_at)}
                </Typography>
                <Chip
                  size="small"
                  label={`${gsc.label} (${succeeded}/${attempted})`}
                  style={gsc.style}
                />
                {dur && (
                  <Typography variant="caption" color="textSecondary">{dur}</Typography>
                )}
              </Box>

              <TableContainer component={Paper} style={{ marginBottom: 8 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Model No</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Images</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" style={{ color: "#999", padding: 16 }}>
                          No items in this run
                        </TableCell>
                      </TableRow>
                    ) : (() => {
                      // Deduplicate by item_id — keep last occurrence (most recent run in bucket)
                      const seen = new Set();
                      const deduped = [];
                      for (let i = groupItems.length - 1; i >= 0; i--) {
                        if (!seen.has(groupItems[i].item_id)) {
                          seen.add(groupItems[i].item_id);
                          deduped.unshift(groupItems[i]);
                        }
                      }
                      return deduped.map((it) => {
                        const label      = it.model_no || `#${it.item_id}`;
                        const productOk  = it.status === "success";
                        const imgStatus  = it.image_sync_status; // "success"|"failed"|"skipped"|null
                        const errorMsg   = it.error_message || it.image_sync_error || null;

                        const productChip = ITEM_STATUS_COLORS[productOk ? "success" : "failed"];
                        const imageChip   = imgStatus ? ITEM_STATUS_COLORS[imgStatus] || ITEM_STATUS_COLORS.failed : null;

                        return (
                          <TableRow key={it.item_id} hover>
                            <TableCell>{label}</TableCell>
                            <TableCell style={{ textTransform: "capitalize" }}>{it.action}</TableCell>
                            <TableCell>
                              <Chip size="small" label={productChip.label} style={productChip.style} />
                            </TableCell>
                            <TableCell>
                              {imageChip
                                ? <Chip size="small" label={imageChip.label} style={imageChip.style} />
                                : <span style={{ color: "#bbb" }}>—</span>}
                            </TableCell>
                            <TableCell className={errorMsg ? classes.errorText : undefined}>
                              {errorMsg || ""}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}